import React from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

import NormalText from '../../../../common/Typography/NormalText'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        label={label}
        inputFormat='dd/MM/yyyy'
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  benefitSummary: '',
  benefitCategory: '',
  benefitProductID: '',
  benefitValue: '',
  benefitUnit: ''
}

const NewBenefitTypeSchema = Yup.object().shape({
  benefitSummary: Yup.string().required('Benefit summary is required'),
  benefitCategory: Yup.string().required('Benefit category is required'),
  benefitProductID: Yup.string().required('Benefit product ID is required'),
  benefitValue: Yup.string().required('Benefit value is required'),
  benefitUnit: Yup.string().required('Benefit unit is required')
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const NewBenefitTypeForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const faberToken = OrbitSession.get('faberToken')
  const { data: catalogIds } = useQuery(['getCatalogs'], async () =>
    RewardsAPI.getCatalogs(faberToken)
  )

  const { mutate: createPromoCode } = useMutation(RewardsAPI.createBenefit, {
    onSuccess: () => {
      toast.success('Benefit type created successfully')
      handleClose()
    },
    onError: () => {
      toast.error('Error creating benefit type')
    }
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewBenefitTypeFormikProps) => {
    const allValues = {
      benefitSummary: values.benefitSummary,
      benefitCategory: values.benefitCategory,
      benefitProductID: values.benefitProductID,
      benefitValue: values.benefitValue,
      benefitUnit: values.benefitUnit,
      faberToken: faberToken
    }
    createPromoCode(allValues)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewBenefitTypeSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Benefit Type Summary</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'benefit-summary'
                  }}
                  name='benefitSummary'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.benefitSummary && touched.benefitSummary)}
                  helperText={
                    errors.benefitSummary && touched.benefitSummary
                      ? errors.benefitSummary
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Benefit Category</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='benefitCategory'
                label=''
              >
                <MenuItem value='DATA'>DATA</MenuItem>
                <MenuItem value='DISCOUNT'>DISCOUNT</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Benefit Product ID</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='benefitProductID'
                label=''
              >
                {catalogIds &&
                  catalogIds.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Benefit Type Value</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'benefit-value'
                  }}
                  name='benefitValue'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.benefitValue && touched.benefitValue)}
                  helperText={
                    errors.benefitValue && touched.benefitValue
                      ? errors.benefitValue
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Benefit Type Unit</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='benefitUnit'
                label=''
              >
                <MenuItem value='DATA'>GB</MenuItem>
                <MenuItem value='DISCOUNT'>ABSOLUTE</MenuItem>
                <MenuItem value='PERCENT'>PERCENT</MenuItem>
              </Select>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button autoFocus onClick={submitForm}>
                Save changes
              </Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewBenefitTypeFormikProps {
  benefitSummary: string
  benefitCategory: string
  benefitProductID: string
  benefitValue: string
  benefitUnit: string
}
